<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import Divider from 'primevue/divider'
import type { Quality } from '@/types/supplier-types'
import { Icon } from '@iconify/vue'
import type { BatchRoll } from '@/types/batch-types'
import EditRollListItem from '@/components/features/batches/form-elements/EditRollListItem.vue'

const props = withDefaults(
  defineProps<{
    quality?: Quality
    includeLength?: boolean
    includeMergeNo?: boolean
    includeSupplierNo?: boolean
    allowAddRolls?: boolean
    lengthLabel?: string
    lengthUnitLabel?: string
    weightLabel?: string
    disabled?: boolean
    includeInactiveRolls?: boolean
    showActions?: boolean
    currentBatchNumber?: string
  }>(),
  {
    includeLength: true,
    includeMergeNo: true,
    includeSupplierNo: false,
    allowAddRolls: false,
    lengthLabel: 'Length',
    lengthUnitLabel: 'Unit',
    weightLabel: 'Weight',
    disabled: true,
    includeInactiveRolls: true,
    showActions: true
  }
)

const model = defineModel<BatchRoll[]>()

const showInactiveRolls = ref(false)

const activeRolls = computed(() => (model.value ? model.value.filter((r) => r.isActive) : []))
const inactiveRolls = computed(() => (model.value ? model.value.filter((r) => !r.isActive) : []))

const toggleShowInactive = () => (showInactiveRolls.value = !showInactiveRolls.value)

const removeRoll = (rollIndex: number) => {
  if (!model.value) return

  // Get the roll from the active rolls array
  // const roll = activeRolls.value[rollIndex]
  const roll = model.value[rollIndex]

  // Find the roll index in the main model value array
  const rollToRemoveIndex = model.value.findIndex((r) => r === roll)

  // Remove the roll
  model.value?.splice(rollToRemoveIndex, 1)
}

const addRolls = (numRolls: number) => {
  model.value = model.value || [] // Initialize if it doesn't exist

  const newRolls = Array.from({ length: numRolls }, () => ({
    rollNumber: '',
    supplierRollNo: '',
    weight: undefined,
    lengthValue: undefined,
    lengthUnit: undefined,
    mergeNumber: '',
    isActive: true,
    rollState: 0
  }))

  model.value.push(...newRolls) // Push all new rolls at once
}

const addRoll = () => addRolls(1)

const addRollOptions = [
  {
    label: 'Add 2 more Rolls',
    command: () => addRolls(2)
  },
  {
    label: 'Add 3 more Rolls',
    command: () => addRolls(3)
  },
  {
    label: 'Add 4 more Rolls',
    command: () => addRolls(4)
  }
]
</script>

<template>
  <div class="grid grid-cols-12 gap-x-20 gap-y-3">
    <div v-for="(roll, index) in model" :key="index" class="col-span-12">
      <h3 class="font-medium">Roll {{ index + 1 }}:</h3>
      <EditRollListItem
        v-model="model![index]"
        @remove="removeRoll(index)"
        :editable="!disabled"
        :show-actions="showActions"
        :current-batch-number="currentBatchNumber"
      />
    </div>

    <div class="col-span-12" v-if="allowAddRolls && !disabled">
      <SplitButton
        size="small"
        outlined
        label="Add Roll"
        @click="addRoll"
        :model="addRollOptions"
      />
    </div>

    <div
      class="col-span-12 flex items-center gap-5"
      v-if="includeInactiveRolls && inactiveRolls.length > 0"
    >
      <Button
        @click="toggleShowInactive"
        severity="secondary"
        outlined
        size="small"
        class="min-w-44 text-xs gap-2"
      >
        <template v-if="!showInactiveRolls">
          <span>Show Inactive Rolls</span>
          <Icon icon="mdi:eye" class="text-xl rounded" />
        </template>

        <template v-else>
          <Icon icon="mdi:eye-off" class="text-xl rounded" />
          <span>Hide Inactive Rolls</span>
        </template>
      </Button>

      <span>({{ inactiveRolls.length }})</span>

      <Divider class="!my-3" />

      <div>
        <Button
          @click="toggleShowInactive"
          rounded
          size="small"
          class="!bg-primary-50 !border-primary-50 !text-primary-500 !w-8 !h-8 !p-0"
        >
          <Icon v-if="!showInactiveRolls" icon="mdi:chevron-up" class="text-4xl" />
          <Icon v-else icon="mdi:chevron-down" class="text-4xl" />
        </Button>
      </div>
    </div>

    <template v-if="showInactiveRolls && inactiveRolls.length > 0">
      <div v-for="(roll, num) in inactiveRolls" :key="num" class="col-span-12">
        <h3 class="font-medium">Roll {{ num }}:</h3>

        <EditRollListItem
            v-model="inactiveRolls[num]"
            :show-actions="false"
            :current-batch-number="currentBatchNumber"
        />
      </div>
    </template>
  </div>
</template>

<style scoped></style>
